import _ from "lodash";

import { getUITranslation } from "../utils/get_ui_translation";
import * as Popup from "./components/popup";
import * as Dialog from "./dialog";
import { enableModifiedWarning } from "./form_utils";
import vex from "./vex_dialog";
import { disableVisualLoader, enableVisualLoader } from "./visual_loader";

const DEFAULT_FILE_MAX_SIZE = 20 * 1024 * 1024;

const getConfig = () => {
  return $(".edit-html").data();
};

const isArray = (a) => Array.isArray(a);

const isObject = (o) => o === Object(o) && !isArray(o) && typeof o !== "function";

const keysToSnake = (o) => {
  if (isObject(o)) {
    const n = {};

    Object.keys(o).forEach((k) => {
      n[_.snakeCase(k)] = keysToSnake(o[k]);
    });

    return n;
  } else if (isArray(o)) {
    return o.map((i) => {
      return keysToSnake(i);
    });
  }

  return o;
};

const onFileUploadedCallback = (resolve) => {
  const { postDocumentUrl, documentForeignKey, documentForeignValue } = getConfig();
  return (blob) => {
    const payload = JSON.stringify([
      {
        type: "editable_component",
        url: blob.url,
        filename: blob.filename,
        mime: blob.mimetype,
        size: blob.size,
        [documentForeignKey]: documentForeignValue,
      },
    ]);

    window.ajax({
      method: "POST",
      url: postDocumentUrl,
      data: { files: payload, skip_adding_to_sequence: true, async: false },
      success: (data) => {
        if (data.success) {
          const response = {
            url: blob.url,
            text: blob.filename,
          };
          if (data.documents[0].wistia) response.wistiaId = data.documents[0].wistia.hashed_id;

          resolve(response);
        } else {
          Dialog.alert(getUITranslation("Erreur"), data.error, "error");
        }
      },
      error: (_xhr, _ajaxOptions, thrownError) => {
        console.error(thrownError);
        Dialog.alert(getUITranslation("Erreur"), getUITranslation("Erreur lors du chargement des documents."), "error");
      },
    });
  };
};

const fileUploadHandler = async (extensions = undefined, maxSize = DEFAULT_FILE_MAX_SIZE) => {
  const filestack = await import("filestack-js");

  return new Promise((resolve, reject) => {
    const { filestackApiKey, filestackS3Bucket, filestackStoragePath } = getConfig();
    filestack
      .init(filestackApiKey)
      .picker({
        lang: window._locale,
        storeTo: {
          location: "S3",
          container: filestackS3Bucket,
          path: filestackStoragePath,
          region: "eu-west-1",
        },
        fromSources: ["local_file_system"],
        maxSize: maxSize,
        maxFiles: 1,
        accept: extensions,
        onFileUploadFinished: onFileUploadedCallback(resolve),
        onFileUploadFailed: ({ code }) => reject(`Chargement impossible (error code: ${code}).`),
      })
      .open();
  });
};

const embedUploaderHandler = (url) => {
  const { embedUploadUrl } = getConfig();

  return new Promise((resolve, reject) => {
    enableVisualLoader();
    ajax({
      method: "POST",
      url: embedUploadUrl,
      data: {
        embed: { url: url },
      },
      success: (data) => {
        disableVisualLoader();
        resolve({
          url: data.url,
          html: data.embedly.html,
          text: data.embedly.provider_name,
        });
      },
      error: (e) => {
        disableVisualLoader();
        reject(e);
      },
      statusCode: {
        400: () => {
          alert(getUITranslation("Le serveur distant a été incapable de répondre à la requête envoyée."));
        },
        404: () => {
          alert(getUITranslation("La ressource ne semble pas exister."));
        },
        500: () => {
          alert(getUITranslation("Une erreur est survenue, veuillez contacter le support."));
        },
      },
    });
  });
};

const mapComponentsUniqUrls = (components) => {
  const urls = components.map((component) => component.content.url).filter((obj) => obj);

  return [...new Set(urls)];
};

const removeDestroyedDocuments = (currentComponents, nextComponents) => {
  const { myMethod, deleteDocumentsUrl } = getConfig();
  if (myMethod !== "POST" || !currentComponents) return;

  const currentUrls = mapComponentsUniqUrls(currentComponents);
  const nextUrls = mapComponentsUniqUrls(nextComponents);
  const urlsDiff = _.difference(currentUrls, nextUrls);
  if (!urlsDiff.length) return;

  ajax({
    method: "DELETE",
    url: deleteDocumentsUrl,
    data: { urls: urlsDiff },
    error: (e) => console.error(e.responseText),
  });
};

const onErrorsUpdate = (errors) => {
  window.componentsEditorErrors = keysToSnake(errors);
};

// The editor calls onComponentsUpdate once on mount with the initial components:
// that first call is the clean baseline the dirty check compares against.
let serializedComponents = null;
let componentsDirty = false;

const markComponentsClean = () => {
  serializedComponents = JSON.stringify(window.editableComponents);
  componentsDirty = false;
};

const onComponentsUpdate = (components) => {
  removeDestroyedDocuments(window.editableComponents, components);
  window.editableComponents = keysToSnake(components);

  if (serializedComponents === null) {
    markComponentsClean();
  } else {
    componentsDirty = JSON.stringify(window.editableComponents) !== serializedComponents;
  }
};

const onSaveSuccess = (result, redirectUrl) => {
  window.location.href = redirectUrl;
};

// Saving now leaves the user on the page, so a second click could send a concurrent save: on a
// creation endpoint that creates a second document.
const setSaveButtonsDisabled = (disabled) => {
  document.querySelectorAll(".js-btn-save, .js-btn-save-and-continue, .js-btn-save-as-template").forEach((button) => {
    button.disabled = disabled;
  });
};

export const onComponentEditorSave = async (ComponentsEditor, options = {}) => {
  const { myMethod, saveUrl, redirectUrl, ownerType } = { ...getConfig(), ...options };

  const onSuccess = options?.onSaveSuccess || onSaveSuccess;
  const data = options?.data || {};
  let save = true;

  setSaveButtonsDisabled(true);

  // TODO: this is a nasty hack to fix an XSS while we wait for a better fix
  // It most likely needs a refactor because there's no html_correction
  // Another option is to stop using contenteditable for the correction, but
  // not sure about potential changes / why it's needed
  const unescapedComponents = JSON.parse(JSON.stringify(window.editableComponents), (key, value) => {
    if (key === "raw_correction") return _.unescape(value);
    return value;
  });

  if (!options?.skipEditableComponents) data[`${ownerType}`] = unescapedComponents;

  const errors = window.componentsEditorErrors;
  if (!_.isEmpty(errors)) {
    await Dialog.alertError("", getUITranslation("Certains éléments sont à corriger"));
    scrollToFirstError(errors);
    save = false;
  }

  const quizzesToReset = window.editableComponents.filter(({ reset_replies }) => reset_replies);
  if (quizzesToReset.length && save) {
    const questions = quizzesToReset.map(({ content }) => `- ${content.raw_question}`);
    const message = [getUITranslation("Les réponses aux quiz seront supprimées pour les quiz suivants\u00A0:")]
      .concat(questions)
      .join("<br/>");

    await Dialog.okCancel("Reset replies", message).then((res) => {
      if (!res) {
        scrollToFirstComponent(quizzesToReset);
        save = false;
      }
    });
  }

  if (!save) {
    setSaveButtonsDisabled(false);
    return;
  }

  ajax({
    method: myMethod || "POST",
    url: saveUrl,
    data: data,
    // Re-enabled before the callbacks run: saving a template chains a second save from its success
    // callback, and that one needs the buttons back.
    success: (result) => {
      setSaveButtonsDisabled(false);

      // `result.success = false` is beeing set in case of error at UserExtranetConfig updating
      if (result.success === false) {
        onRequestFail(ComponentsEditor, result.error_message);
      } else {
        markComponentsClean();
        onSuccess(result, redirectUrl);
      }
    },
    error: (e) => {
      setSaveButtonsDisabled(false);
      onRequestFail(ComponentsEditor, e.responseText);
    },
  });
};

const scrollToFirstError = (errors) => {
  const ids = Object.keys(errors).map((uuid) => uuid.replaceAll("_", ""));

  for (const elem of document.querySelectorAll("[data-order]")) {
    if (ids.includes(elem.getAttribute("data-uuid")?.replaceAll("-", ""))) {
      window.scrollTo(0, elem.offsetTop - 80);
      break;
    }
  }
};

const scrollToFirstComponent = (components) => {
  for (const elem of document.querySelectorAll("[data-order]")) {
    if (components.map(({ uuid }) => uuid).includes(elem.getAttribute("data-uuid"))) {
      window.scrollTo(0, elem.offsetTop - 80);
      break;
    }
  }
};

const onRequestFail = (ComponentsEditor, message) => {
  ComponentsEditor.revalidate();
  Dialog.alert("", message, "error");
  setTimeout(() => {
    scrollToFirstError(window.componentsEditorErrors);
  }, 100);
};

const withHasReplies = (components) =>
  components.map((component) => ({
    ...component,
    has_replies: Boolean(component?.editable_component_quiz_replies?.length),
  }));

export const initComponentsEditor = (ComponentsEditor) => {
  const { editableComponents, replayable, componentsType, enableVerificationSwitch, verifiable, removableTitle, limitations } =
    getConfig();
  const updatedComponents = withHasReplies(editableComponents);

  // TODO: this is a nasty hack to fix an XSS while we wait for a better fix
  // It most likely needs a refactor because there's no html_correction
  // Another option is to stop using contenteditable for the correction, but
  // not sure about potential changes / why it's needed
  const escapedComponents = JSON.parse(JSON.stringify(updatedComponents), (key, value) => {
    if (key === "raw_correction") return _.escape(value);
    return value;
  });

  ComponentsEditor.init({
    elementId: "component-editor",
    componentsPayload: escapedComponents,
    onComponentsUpdate: onComponentsUpdate,
    onErrorsUpdate: onErrorsUpdate,
    config: {
      limitations,
      showFomoModalFn: () => {
        const popup = document.getElementById("js-lms-ece-limitations");
        if (popup) Popup.open(popup);
      },
      componentsType: componentsType || "all",
      enableVerificationSwitch: enableVerificationSwitch,
      isVerifiable: verifiable,
      isReplayable: replayable,
      removableTitle: removableTitle,
      language: window._locale,
      wrapperClassName: "content-editor-wrapper",
      filePicker: fileUploadHandler,
      embedUploader: embedUploaderHandler,
      confirmDialog: (message) => {
        return new Promise((resolve) => {
          vex.dialog.confirm({
            message,
            callback: (value) => resolve(value),
          });
        });
      },
    },
  });

  enableModifiedWarning(() => componentsDirty);
};
